import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger, Button } from '@creanizer/ui'
import { Save, Download, Edit } from 'lucide-react'
import type { WorldBuilderResult } from '@creanizer/types'
import { WorldBuilderEditModal } from '../WorldBuilderEditModal'
import { WorldBasicInfo } from './WorldBasicInfo'
import { WorldParameters } from './WorldParameters'
import { WorldParameterRadar } from './WorldParameterRadar'
import { WorldTimeline } from './WorldTimeline'
import { WorldCultures } from './WorldCultures'
import { WorldGeography } from './WorldGeography'
import { WorldMagicSystem } from './WorldMagicSystem'
import { WorldTechnology } from './WorldTechnology'
import { WorldUniqueFeatures } from './WorldUniqueFeatures'
import { WorldThreats } from './WorldThreats'

interface WorldBuilderDisplayProps {
  result: WorldBuilderResult
  onSave: () => void
  isSaved: boolean
  onEdit?: (updatedResult: Partial<WorldBuilderResult>) => void
}

export function WorldBuilderDisplay({ 
  result, 
  onSave, 
  isSaved, 
  onEdit
}: WorldBuilderDisplayProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  // Null/undefined check
  if (!result) {
    return (
      <div className="card">
        <div className="p-6">
          <p className="text-muted-foreground">結果が生成されていません</p>
        </div>
      </div>
    )
  }
  const handleDownload = () => {
    const text = formatWorldAsText(result)
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `world_${result.id}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  
  return (
    <div className="card">
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="order-2 sm:order-1">
            <h2 className="text-2xl font-bold">{result?.name || '生成中...'}</h2>
            <p className="text-muted-foreground mt-1">{result?.concept || ''}</p>
          </div>
          <div className="flex gap-2 order-1 sm:order-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              title="ダウンロード"
            >
              <Download className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">ダウンロード</span>
            </Button>
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditModalOpen(true)}
                title="編集"
              >
                <Edit className="h-4 w-4" />
                <span className="ml-2 hidden sm:inline">編集</span>
              </Button>
            )}
            <Button
              variant={isSaved ? 'outline' : 'default'}
              size="sm"
              onClick={onSave}
              disabled={isSaved}
              title={isSaved ? '保存済み' : '保存'}
            >
              <Save className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">{isSaved ? '保存済み' : '保存'}</span>
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="basic" className="mt-6">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 gap-1 h-auto">
            <TabsTrigger value="basic" className="text-xs sm:text-sm">基本</TabsTrigger>
            <TabsTrigger value="parameters" className="text-xs sm:text-sm">パラメータ</TabsTrigger>
            <TabsTrigger value="unique" className="text-xs sm:text-sm">独自要素</TabsTrigger>
            <TabsTrigger value="history" className="text-xs sm:text-sm">歴史・文化</TabsTrigger>
            <TabsTrigger value="world" className="text-xs sm:text-sm">世界設定</TabsTrigger>
            <TabsTrigger value="systems" className="text-xs sm:text-sm">システム</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic" className="mt-4">
            <div className="space-y-6">
              <WorldBasicInfo result={result} />
              {result?.details?.threats && (
                <div>
                  <h3 className="font-semibold mb-3">脅威と危険</h3>
                  <WorldThreats 
                    threats={result.details.threats} 
                    dangerLevel={result?.parameters?.dangerLevel || 5}
                  />
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="parameters" className="mt-4">
            <div className="space-y-6">
              {result?.parameters ? (
                <>
                  <WorldParameters parameters={result.parameters} />
                  <WorldParameterRadar parameters={result.parameters} />
                </>
              ) : (
                <p className="text-muted-foreground">パラメータデータがありません</p>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="unique" className="mt-4">
            {result?.uniqueFeatures ? (
              <WorldUniqueFeatures uniqueFeatures={result.uniqueFeatures} />
            ) : (
              <p className="text-muted-foreground">独自要素データがありません</p>
            )}
          </TabsContent>
          
          <TabsContent value="history" className="mt-4">
            <div className="space-y-6">
              {result?.details?.timeline && (
                <div>
                  <h3 className="font-semibold mb-3">歴史年表</h3>
                  <WorldTimeline events={result.details.timeline} />
                </div>
              )}
              {result?.details?.cultures && result.details.cultures.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">文化</h3>
                  <WorldCultures cultures={result.details.cultures} />
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="world" className="mt-4">
            {result?.details?.geography ? (
              <WorldGeography geography={result.details.geography} />
            ) : (
              <p className="text-muted-foreground">地理データがありません</p>
            )}
          </TabsContent>
          
          <TabsContent value="systems" className="mt-4">
            <div className="space-y-6">
              {result?.details?.magicSystem && (
                <div>
                  <h3 className="font-semibold mb-3">魔法体系</h3>
                  <WorldMagicSystem magicSystem={result.details.magicSystem} />
                </div>
              )}
              {result?.details?.technology && (
                <div>
                  <h3 className="font-semibold mb-3">技術体系</h3>
                  <WorldTechnology technology={result.details.technology} />
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {isEditModalOpen && onEdit && (
        <WorldBuilderEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={(updatedResult) => {
            onEdit(updatedResult)
            setIsEditModalOpen(false)
          }}
          worldResult={result}
          title={`「${result.name}」を編集`}
        />
      )}
    </div>
  )
}

function formatWorldAsText(result: WorldBuilderResult): string {
  const lines: string[] = []
  
  // Null check
  if (!result) {
    return '結果がありません'
  }
  
  // ヘッダー
  lines.push(`【世界観】${result?.name || '名称未設定'}`)
  lines.push('━'.repeat(50))
  lines.push('')
  
  // 基本情報
  lines.push('■ 基本情報')
  lines.push(`コンセプト: ${result?.concept || ''}`)
  lines.push(`概要: ${result?.summary || ''}`)
  lines.push(`ジャンル: ${result?.genres?.join('、') || ''}`)
  lines.push(`世界の規模: ${result?.worldScale || ''}`)
  lines.push('')
  
  // パラメータ（全項目）
  if (result?.parameters) {
    lines.push('■ 世界観パラメータ')
    lines.push(`魔法レベル: ${result.parameters.magicLevel || 0}/10`)
    lines.push(`科学技術レベル: ${result.parameters.scienceLevel || 0}/10`)
    lines.push(`文明レベル: ${result.parameters.civilizationLevel || 0}/10`)
    lines.push(`危険度: ${result.parameters.dangerLevel || 0}/10`)
    lines.push(`神秘性: ${result.parameters.mysteryLevel || 0}/10`)
    lines.push(`複雑さ: ${result.parameters.complexityLevel || 0}/10`)
    lines.push(`独自性: ${result.parameters.uniquenessLevel || 0}/10`)
    lines.push(`混沌度: ${result.parameters.chaosLevel || 0}/10`)
  }
  lines.push('')
  
  // 独自要素
  if (result?.uniqueFeatures) {
    lines.push('■ 独自要素')
    lines.push(`シグネチャー: ${result.uniqueFeatures.signature || ''}`)
    if (result.uniqueFeatures.laws?.length > 0) {
      lines.push('独自法則:')
      result.uniqueFeatures.laws.forEach(law => lines.push(`  ・${law}`))
    }
    if (result.uniqueFeatures.paradoxes && result.uniqueFeatures.paradoxes.length > 0) {
      lines.push('パラドックス:')
      result.uniqueFeatures.paradoxes.forEach(p => lines.push(`  ・${p}`))
    }
    if (result.uniqueFeatures.mysteries && result.uniqueFeatures.mysteries.length > 0) {
      lines.push('謎:')
      result.uniqueFeatures.mysteries.forEach(m => lines.push(`  ・${m}`))
    }
    if (result.uniqueFeatures.innovations && result.uniqueFeatures.innovations.length > 0) {
      lines.push('革新:')
      result.uniqueFeatures.innovations.forEach(i => lines.push(`  ・${i}`))
    }
    lines.push('')
  }
  
  // 時代設定
  if (result.details.era) {
    lines.push('■ 時代設定')
    lines.push(`時代名: ${result.details.era.name}`)
    lines.push(`説明: ${result.details.era.description}`)
    lines.push(`技術レベル: ${result.details.era.technologyLevel}`)
    lines.push(`社会構造: ${result.details.era.socialStructure}`)
    lines.push('')
  }
  
  // 地理
  if (result.details.geography) {
    lines.push('■ 地理')
    lines.push(`概要: ${result.details.geography.description}`)
    if ((result.details.geography as any).worldSize) {
      lines.push(`世界の規模: ${(result.details.geography as any).worldSize}`)
    }
    lines.push(`気候: ${result.details.geography.climate}`)
    if (result.details.geography.terrain && result.details.geography.terrain.length > 0) {
      lines.push('地形:')
      result.details.geography.terrain.forEach(t => {
        if (typeof t === 'object' && t !== null) {
          lines.push(`  ・${(t as any).name}: ${(t as any).description}`)
        } else {
          lines.push(`  ・${t}`)
        }
      })
    }
    if (result.details.geography.naturalResources && result.details.geography.naturalResources.length > 0) {
      lines.push('天然資源:')
      result.details.geography.naturalResources.forEach(r => {
        if (typeof r === 'object' && r !== null) {
          lines.push(`  ・${(r as any).name}: ${(r as any).description}`)
        } else {
          lines.push(`  ・${r}`)
        }
      })
    }
    if (result.details.geography.notableLocations && result.details.geography.notableLocations.length > 0) {
      lines.push('重要な場所:')
      result.details.geography.notableLocations.forEach(l => {
        if (typeof l === 'object' && l !== null) {
          lines.push(`  ・${(l as any).name}: ${(l as any).description}`)
        } else {
          lines.push(`  ・${l}`)
        }
      })
    }
    if ((result.details.geography as any).dangerousZones && (result.details.geography as any).dangerousZones.length > 0) {
      lines.push('危険地帯:')
      ;(result.details.geography as any).dangerousZones.forEach((z: any) => {
        lines.push(`  ・${z.name}: ${z.description}`)
      })
    }
    lines.push('')
  }
  
  // 脅威と危険
  if (result.details.threats) {
    lines.push('■ 脅威と危険')
    
    if (result.details.threats.existsHostileForce && result.details.threats.hostileForces) {
      lines.push('【敵性存在】')
      result.details.threats.hostileForces.forEach(force => {
        lines.push(`◆ ${force.name}（${force.type}）`)
        lines.push(`  説明: ${force.description}`)
        lines.push(`  脅威: ${force.threat}`)
        lines.push(`  能力: ${force.capabilities.join('、')}`)
        if (force.weaknesses && force.weaknesses.length > 0) {
          lines.push(`  弱点: ${force.weaknesses.join('、')}`)
        }
        if (force.samples && force.samples.length > 0) {
          lines.push(`  個体例:`)
          force.samples.forEach(s => lines.push(`    ・${s}`))
        }
        lines.push('')
      })
    }
    
    if (result.details.threats.conflictSituation) {
      if (result.details.threats.conflictSituation.currentWars && result.details.threats.conflictSituation.currentWars.length > 0) {
        lines.push('【現在の戦争】')
        result.details.threats.conflictSituation.currentWars.forEach(war => {
          lines.push(`◆ ${war.name}`)
          lines.push(`  参加勢力: ${war.participants.join(' vs ')}`)
          lines.push(`  原因: ${war.cause}`)
          lines.push(`  現状: ${war.status}`)
          lines.push(`  影響: ${war.impact}`)
        })
        lines.push('')
      }
      
      if (result.details.threats.conflictSituation.tensions && result.details.threats.conflictSituation.tensions.length > 0) {
        lines.push('【緊張状態】')
        result.details.threats.conflictSituation.tensions.forEach(t => lines.push(`  ・${t}`))
        lines.push('')
      }
      
      if (result.details.threats.conflictSituation.dangerZones && result.details.threats.conflictSituation.dangerZones.length > 0) {
        lines.push('【危険地帯】')
        result.details.threats.conflictSituation.dangerZones.forEach(z => lines.push(`  ・${z}`))
        lines.push('')
      }
      
      if (result.details.threats.conflictSituation.securityMeasures && result.details.threats.conflictSituation.securityMeasures.length > 0) {
        lines.push('【安全対策】')
        result.details.threats.conflictSituation.securityMeasures.forEach(m => lines.push(`  ・${m}`))
        lines.push('')
      }
    }
    
    if (result.details.threats.worldDangers && result.details.threats.worldDangers.length > 0) {
      lines.push('【世界的な危機】')
      result.details.threats.worldDangers.forEach(d => lines.push(`  ・${d}`))
      lines.push('')
    }
  }
  
  // 文化
  if (result.details.cultures && result.details.cultures.length > 0) {
    lines.push('■ 文化')
    result.details.cultures.forEach((culture, index) => {
      lines.push(`◆ ${culture.name}`)
      lines.push(`  説明: ${culture.description}`)
      if (culture.population) lines.push(`  人口: ${culture.population}`)
      if (culture.language) lines.push(`  言語: ${culture.language}`)
      if (culture.governance) lines.push(`  統治: ${culture.governance}`)
      if (culture.architecture) lines.push(`  建築: ${culture.architecture}`)
      if (culture.clothing) lines.push(`  服装: ${culture.clothing}`)
      if (culture.values.length > 0) {
        lines.push(`  価値観: ${culture.values.join('、')}`)
      }
      if (culture.customs.length > 0) {
        lines.push(`  習慣:`)
        culture.customs.forEach(c => lines.push(`    ・${c}`))
      }
      if (culture.taboos.length > 0) {
        lines.push(`  タブー:`)
        culture.taboos.forEach(t => lines.push(`    ・${t}`))
      }
      if (index < result.details.cultures.length - 1) lines.push('')
    })
    lines.push('')
  }
  
  // 歴史年表
  if (result.details.timeline && result.details.timeline.length > 0) {
    lines.push('■ 歴史年表')
    result.details.timeline.forEach(event => {
      lines.push(`◆ ${event.year}: ${event.event}`)
      if ((event as any).description) {
        lines.push(`  説明: ${(event as any).description}`)
      }
      lines.push(`  影響度: ${event.impact}`)
      lines.push(`  カテゴリ: ${event.category}`)
      if ((event as any).consequences && (event as any).consequences.length > 0) {
        lines.push(`  結果:`)
        ;(event as any).consequences.forEach((c: string) => lines.push(`    ・${c}`))
      }
      lines.push('')
    })
  }
  
  // 魔法体系
  if (result.details.magicSystem && result.details.magicSystem.exists) {
    lines.push('■ 魔法体系')
    lines.push(`説明: ${result.details.magicSystem.description}`)
    if (result.details.magicSystem.rules && result.details.magicSystem.rules.length > 0) {
      lines.push('ルール:')
      result.details.magicSystem.rules.forEach(r => {
        if (typeof r === 'object' && r !== null) {
          lines.push(`  ・${(r as any).name}: ${(r as any).description}`)
        } else {
          lines.push(`  ・${r}`)
        }
      })
    }
    if (result.details.magicSystem.limitations && result.details.magicSystem.limitations.length > 0) {
      lines.push('制限:')
      result.details.magicSystem.limitations.forEach(l => {
        if (typeof l === 'object' && l !== null) {
          lines.push(`  ・${(l as any).name}: ${(l as any).description}`)
        } else {
          lines.push(`  ・${l}`)
        }
      })
    }
    if (result.details.magicSystem.practitioners && result.details.magicSystem.practitioners.length > 0) {
      lines.push('実践者:')
      result.details.magicSystem.practitioners.forEach(p => {
        if (typeof p === 'object' && p !== null) {
          lines.push(`  ・${(p as any).name}: ${(p as any).description}`)
        } else {
          lines.push(`  ・${p}`)
        }
      })
    }
    lines.push('')
  }
  
  // 技術体系
  if (result.details.technology) {
    lines.push('■ 技術体系')
    lines.push(`レベル: ${result.details.technology.level}`)
    if (result.details.technology.keyTechnologies && result.details.technology.keyTechnologies.length > 0) {
      lines.push('主要技術:')
      result.details.technology.keyTechnologies.forEach(t => {
        if (typeof t === 'object' && t !== null) {
          lines.push(`  ・${(t as any).name}: ${(t as any).description}`)
        } else {
          lines.push(`  ・${t}`)
        }
      })
    }
    if (result.details.technology.limitations && result.details.technology.limitations.length > 0) {
      lines.push('制限:')
      result.details.technology.limitations.forEach(l => {
        if (typeof l === 'object' && l !== null) {
          lines.push(`  ・${(l as any).name}: ${(l as any).description}`)
        } else {
          lines.push(`  ・${l}`)
        }
      })
    }
    if (result.details.technology.uniqueAspects && result.details.technology.uniqueAspects.length > 0) {
      lines.push('独自の側面:')
      result.details.technology.uniqueAspects.forEach(a => {
        if (typeof a === 'object' && a !== null) {
          lines.push(`  ・${(a as any).name}: ${(a as any).description}`)
        } else {
          lines.push(`  ・${a}`)
        }
      })
    }
    lines.push('')
  }
  
  
  // 一貫性チェック
  if (result.consistency) {
    lines.push('■ 一貫性チェック')
    lines.push(`スコア: ${result.consistency.score}/100`)
    if (result.consistency.issues && result.consistency.issues.length > 0) {
      lines.push('問題点:')
      result.consistency.issues.forEach(i => lines.push(`  ・${i}`))
    }
    if (result.consistency.suggestions && result.consistency.suggestions.length > 0) {
      lines.push('改善提案:')
      result.consistency.suggestions.forEach(s => lines.push(`  ・${s}`))
    }
    lines.push('')
  }
  
  // メタデータ
  lines.push('■ メタデータ')
  lines.push(`生成日時: ${result.metadata.createdAt}`)
  lines.push(`更新日時: ${result.metadata.updatedAt}`)
  lines.push(`バージョン: ${result.metadata.version}`)
  lines.push(`生成モード: ${result.metadata.generationMode}`)
  
  return lines.join('\n')
}